/**
 * Collection Filters with AJAX Support
 * Handles real-time filtering without page reloads
 */

class CollectionFilters extends HTMLElement {
  constructor() {
    super();
    this.filterForm = this.querySelector('.filter-form');
    this.productContainer = document.querySelector('.collection-products');
    this.activeFiltersContainer = document.querySelector('.active-filters');
    this.isUpdating = false;

    this.init();
  }

  init() {
    if (!this.filterForm) return;

    // Handle form submission
    this.filterForm.addEventListener(
      'submit',
      this.handleFormSubmit.bind(this)
    );

    // Handle individual filter changes
    this.filterForm.addEventListener(
      'change',
      this.handleFilterChange.bind(this)
    );

    // Handle direct filter links (from active filters)
    document.addEventListener('click', this.handleFilterLink.bind(this));

    // Handle browser back/forward
    window.addEventListener('popstate', this.handlePopState.bind(this));

    // Initialize accordion behavior
    this.initAccordion();

    // Initialize drawer behavior
    this.initDrawer();

    // Initialize disabled filters on page load
    this.updateDisabledFilters();
  }

  initAccordion() {
    // Check if accordion style is enabled for desktop or mobile
    const hasDesktopAccordion = this.classList.contains(
      'filter-style-desktop--accordion'
    );
    const hasMobileAccordion = this.classList.contains(
      'filter-style-mobile--accordion'
    );

    if (!hasDesktopAccordion && !hasMobileAccordion) return;

    const filterGroups = this.querySelectorAll('.filter-group');

    filterGroups.forEach((group) => {
      const header = group.querySelector('h4');
      if (!header) return;

      // Open first group by default
      if (group === filterGroups[0]) {
        group.classList.add('is-open');
      }

      header.addEventListener('click', () => {
        group.classList.toggle('is-open');
      });
    });
  }

  updateDisabledFilters() {
    // Find all filter options and update their disabled state based on count
    const filterOptions = this.querySelectorAll('.filter-option');

    filterOptions.forEach((option) => {
      const checkbox = option.querySelector('input[type="checkbox"]');
      const countElement = option.querySelector('.filter-count');

      if (!checkbox || !countElement) return;

      // Extract count from text like "(0)" or "(5)"
      const countText = countElement.textContent || countElement.innerText;
      const count = parseInt(countText.replace(/[()]/g, '')) || 0;
      const isActive = checkbox.checked;

      // Disable if count is 0 and not currently active
      const shouldDisable = count === 0 && !isActive;

      // Update checkbox disabled state
      checkbox.disabled = shouldDisable;

      // Update CSS classes
      if (shouldDisable) {
        option.classList.add('filter-option--disabled');
      } else {
        option.classList.remove('filter-option--disabled');
      }
    });
  }

  initDrawer() {
    const desktopDrawer = document.querySelector(
      '.collection-layout-desktop--drawer'
    );
    const mobileDrawer = document.querySelector(
      '.collection-layout-mobile--drawer'
    );

    if (!desktopDrawer && !mobileDrawer) return;

    // Get existing HTML elements
    const toggleButton = document.querySelector('.filter-toggle');
    const closeButton = document.querySelector('.drawer-close');
    const overlay = document.querySelector('.filter-overlay');

    if (!toggleButton || !closeButton || !overlay) return;

    // Toggle drawer
    toggleButton.addEventListener('click', () => {
      this.openDrawer();
    });

    // Close drawer
    overlay.addEventListener('click', this.closeDrawer.bind(this));
    closeButton.addEventListener('click', this.closeDrawer.bind(this));

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeDrawer();
      }
    });
  }

  openDrawer() {
    this.classList.add('is-open');
    const overlay = document.querySelector('.filter-overlay');
    if (overlay) {
      overlay.classList.add('is-open');
    }
    document.body.style.overflow = 'hidden';
  }

  closeDrawer() {
    this.classList.remove('is-open');
    const overlay = document.querySelector('.filter-overlay');
    if (overlay) {
      overlay.classList.remove('is-open');
    }
    document.body.style.overflow = '';
  }

  // Check if drawer functionality should be active based on screen size and layout
  isDrawerActive() {
    const isMobile = window.innerWidth <= 768;
    const hasDesktopDrawer = document.querySelector(
      '.collection-layout-desktop--drawer'
    );
    const hasMobileDrawer = document.querySelector(
      '.collection-layout-mobile--drawer'
    );

    if (isMobile && hasMobileDrawer) return true;
    if (!isMobile && hasDesktopDrawer) return true;
    return false;
  }

  handleFormSubmit(event) {
    // Prevent default form submission since we handle everything via AJAX
    event.preventDefault();
  }

  handleFilterChange(event) {
    // Prevent disabled filters from being changed
    if (event.target.disabled) {
      event.preventDefault();
      event.target.checked = false;
      return;
    }

    // Optional: Auto-apply filters on change instead of waiting for submit
    if (event.target.type === 'checkbox') {
      // Small delay to allow for multiple quick selections
      clearTimeout(this.changeTimeout);
      this.changeTimeout = setTimeout(() => {
        this.updateFilters();
      }, 500);
    }
  }

  handleFilterLink(event) {
    // Handle clicks on active filter remove links
    if (
      event.target.closest('.active-filter') &&
      event.target.tagName === 'A'
    ) {
      event.preventDefault();
      const url = event.target.getAttribute('href');
      this.loadFilteredResults(url);
    }
  }

  async updateFilters() {
    if (this.isUpdating) return;

    this.isUpdating = true;
    this.showLoadingState();

    try {
      const formData = new FormData(this.filterForm);
      const url = this.buildFilterURL(formData);

      await this.loadFilteredResults(url);
    } catch (error) {
      console.error('Filter update error:', error);
      // Fallback to regular form submission
      this.filterForm.submit();
    } finally {
      this.isUpdating = false;
      this.hideLoadingState();
    }
  }

  buildFilterURL(formData) {
    const params = new URLSearchParams();

    for (const [name, value] of formData.entries()) {
      if (value && value.trim() !== '') {
        params.append(name, value);
      }
    }

    const currentURL = new URL(window.location);
    currentURL.search = params.toString();

    return currentURL.toString();
  }

  async loadFilteredResults(url) {
    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Update products
      const newProducts = doc.querySelector('.collection-products');
      if (newProducts && this.productContainer) {
        this.productContainer.innerHTML = newProducts.innerHTML;
      }

      // Update active filters
      const newActiveFilters = doc.querySelector('.active-filters');
      if (this.activeFiltersContainer) {
        if (newActiveFilters) {
          this.activeFiltersContainer.innerHTML = newActiveFilters.innerHTML;
        } else {
          this.activeFiltersContainer.innerHTML = '';
        }
      }

      // Update filter form checkboxes to match current state
      this.updateFilterFormState(doc);

      // Update filter counts from server response
      this.updateFilterCounts(doc);

      // Update URL without page reload
      if (url !== window.location.href) {
        window.history.pushState({}, '', url);
      }

      // Reinitialize interactive features after content update
      this.initAccordion();

      // Update disabled states for filters with 0 count (now with updated counts)
      this.updateDisabledFilters();
    } catch (error) {
      console.error('Failed to load filtered results:', error);
      throw error;
    }
  }

  updateFilterFormState(doc) {
    // Update checkboxes to match the current filtered state
    const newForm = doc.querySelector('.filter-form');
    if (!newForm) return;

    const newCheckboxes = newForm.querySelectorAll('input[type="checkbox"]');

    newCheckboxes.forEach((newCheckbox) => {
      const currentCheckbox = this.filterForm.querySelector(
        `input[name="${newCheckbox.name}"][value="${newCheckbox.value}"]`
      );

      if (currentCheckbox) {
        currentCheckbox.checked = newCheckbox.checked;
      }
    });
  }

  updateFilterCounts(doc) {
    // Update filter counts from the new document
    const newFilterForm = doc.querySelector('.filter-form');
    if (!newFilterForm) return;

    // Get all filter count elements from the new document
    const newCounts = newFilterForm.querySelectorAll('.filter-count');
    const currentCounts = this.querySelectorAll('.filter-count');

    // Update each count element
    newCounts.forEach((newCount, index) => {
      const currentCount = currentCounts[index];
      if (currentCount) {
        currentCount.textContent = newCount.textContent;
      }
    });
  }

  showLoadingState() {
    if (this.productContainer) {
      this.productContainer.style.opacity = '0.6';
      this.productContainer.style.pointerEvents = 'none';
    }

    // Add loading spinner or text
    const loadingIndicator = document.createElement('div');
    loadingIndicator.className = 'filter-loading';
    loadingIndicator.textContent = 'Updating filters...';
    loadingIndicator.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(0,0,0,0.8);
      color: white;
      padding: 1rem 2rem;
      border-radius: 4px;
      z-index: 1000;
    `;
    document.body.appendChild(loadingIndicator);
  }

  hideLoadingState() {
    if (this.productContainer) {
      this.productContainer.style.opacity = '1';
      this.productContainer.style.pointerEvents = 'auto';
    }

    const loadingIndicator = document.querySelector('.filter-loading');
    if (loadingIndicator) {
      loadingIndicator.remove();
    }
  }

  handlePopState(event) {
    // Handle browser back/forward buttons
    window.location.reload();
  }
}

// Register the custom element
customElements.define('collection-filters', CollectionFilters);
